<template>
  <b-dropdown
    ref="dropdown"
    :id="id"
    class="app-select"
    :class="rootClasses"
    variant="link"
    :disabled="disabled"
    :dropup="dropup"
    :toggle-class="['app-select__toggle', toggleClass]"
    :menu-class="['app-select__menu', { 'app-select__menu--inline': inline }, menuClass]"
    lazy
    no-caret
    @show="onShow"
    @shown="onShown"
    @hidden="onHidden"
  >
    <!-- Die Mehrfachauswahl zeigt ohne placeholder den Titel mit der Anzahl, sonst die gewählten Texte -->
    <!-- Mehrere Werte werden kommasepariert dargestellt, nicht als Chips -->
    <template #button-content>
      <slot name="selected" :options="selectedOptions" :values="selectedValues" :text="selectedText">
        <span
          v-if="showLabelWithCount"
          class="app-select__label"
          :class="{ 'app-select__label--placeholder': !selectionCount }"
        >
          {{ label }}
          <span v-if="selectionCount" class="app-select__count">({{ selectionCount }})</span>
        </span>
        <span
          v-else
          class="app-select__label app-select__label--ellipsis"
          :class="{ 'app-select__label--placeholder': !selectionCount }"
        >
          {{ selectionCount ? selectedText : placeholder || '—' }}
        </span>
      </slot>

      <span class="app-select__indicators">
        <b-spinner v-if="loading" small class="app-select__spinner" />
        <!-- Der Klick auf das X darf das Dropdown nicht öffnen, deshalb wird das Event gestoppt -->
        <span
          v-if="showClear"
          class="app-select__clear"
          role="button"
          tabindex="-1"
          title="Auswahl entfernen"
          @click.stop.prevent="onClear"
          @mousedown.stop.prevent
        >
          <X :size="14" />
        </span>
        <ChevronUp v-if="isOpen" :size="16" class="app-select__chevron" />
        <ChevronDown v-else :size="16" class="app-select__chevron" />
      </span>
    </template>

    <div class="app-select__panel" @keydown="onKeydown">
      <div v-if="!hideSearch" class="app-select__search">
        <Search :size="15" class="app-select__search-icon" />
        <b-form-input
          ref="searchInput"
          v-model="search"
          class="app-select__search-input"
          :placeholder="effectiveSearchPlaceholder"
          autocomplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded="true"
          :aria-controls="listId"
          :aria-activedescendant="highlightedOptionId"
        />
      </div>

      <div v-if="showBulkActions" class="app-select__actions">
        <button
          type="button"
          class="app-select__action"
          :disabled="isAllVisibleSelected"
          @click="selectAllVisible"
        >
          Alle auswählen
        </button>
        <button
          type="button"
          class="app-select__action app-select__action--danger"
          :disabled="!hasVisibleSelection"
          @click="clearVisible"
        >
          Alle entfernen
        </button>
      </div>

      <div
        ref="list"
        :id="listId"
        class="app-select__list"
        :style="{ maxHeight: listMaxHeight }"
        tabindex="-1"
        role="listbox"
        :aria-multiselectable="multiple ? 'true' : 'false'"
      >
        <button
          v-for="(option, index) in visibleOptions"
          :key="option.key"
          :id="optionId(index)"
          type="button"
          :class="[
            option.isGroup ? 'app-select__group' : 'app-select__option',
            {
              'app-select__option--active': index === highlightedIndex,
              'app-select__option--selected': !option.isGroup && isChecked(option),
              'app-select__option--disabled': option.disabled,
            },
          ]"
          :role="option.isGroup ? 'presentation' : 'option'"
          :aria-selected="option.isGroup ? null : isChecked(option) ? 'true' : 'false'"
          :disabled="!isSelectable(option)"
          @click.stop="toggleOption(option)"
          @mousedown.prevent
          @mouseenter="highlightedIndex = index"
        >
          <!-- Der Gruppentitel wählt bei Mehrfachauswahl die komplette Gruppe -->
          <template v-if="option.isGroup">
            <span
              v-if="multiple"
              class="app-select__checkbox"
              :class="{ 'app-select__checkbox--checked': isChecked(option) }"
            >
              <Check v-if="isChecked(option)" :size="12" :stroke-width="3" />
            </span>
            <span class="app-select__group-text">{{ option.text }}</span>
          </template>
          <slot
            v-else
            name="option"
            :option="option"
            :index="index"
            :selected="isChecked(option)"
            :active="index === highlightedIndex"
            :search="search"
          >
            <span
              class="app-select__checkbox"
              :class="{
                'app-select__checkbox--checked': isChecked(option),
                'app-select__checkbox--radio': !multiple,
              }"
            >
              <Check v-if="isChecked(option)" :size="12" :stroke-width="3" />
            </span>
            <slot name="option-label" :option="option" :selected="isChecked(option)">
              <span class="app-select__option-text">{{ option.text }}</span>
            </slot>
          </slot>
        </button>

        <div v-if="loading && !visibleOptions.length" class="app-select__empty">
          <b-spinner small class="mr-2" />
          Lädt …
        </div>
        <div v-else-if="!visibleOptions.length && emptyMessage" class="app-select__empty">
          {{ emptyMessage }}
        </div>
      </div>

      <b-button
        v-if="usesApplyButton"
        variant="primary"
        class="dialog-btn app-select__apply"
        block
        @click="onApply"
      >
        {{ applyText }}
      </b-button>
    </div>
  </b-dropdown>
</template>

<script>
import { Search, Check, ChevronDown, ChevronUp, X } from 'lucide-vue';
import {
  APPLY_MODES,
  SORT_MODES,
  SIZES,
  asText,
  toValueArray,
  buildOptions,
  sortOptions,
  pinSelectedFirst,
  filterOptions,
  findAdjacentFocusable,
  openAppSelectTrigger,
} from './utils';

// Generische Select-Komponente mit Suche, Einfach- und Mehrfachauswahl
// Die Aufbereitung der Optionen liegt in ./utils.js
//
// Die Optionen laufen durch zwei Ketten, jede Stufe ist ein Computed:
// options → normalizedOptions (flach und vereinheitlicht) → sortedOptions → visibleOptions
// value → selectedValues (angewendet) → currentValues (mit Anwenden-Button der Draft)

export default {
  name: 'AppSelectComponent',
  components: { Search, Check, ChevronDown, ChevronUp, X },
  props: {
    // Gewählter Wert, im Einfachmodus ein Skalar, mit multiple ein Array von Werten
    // Absichtlich ohne type, damit Zahlen, Booleans und null ohne Warnung erlaubt sind
    value: { default: null },
    // Optionen als { value, text, disabled } oder als einfache Strings bzw. Zahlen
    // Mit groupLabel und groupValues stattdessen eine Liste von Gruppen
    options: { type: Array, default: () => [] },
    // Mehrfachauswahl
    multiple: { type: Boolean, default: false },
    // Titel des Feldes, ohne placeholder steht er mit der Anzahl im Trigger
    label: { type: String, default: '' },
    // Platzhalter im Trigger, bei Auswahl werden stattdessen die gewählten Texte gezeigt
    placeholder: { type: String, default: '' },
    // Feldnamen innerhalb der Optionsobjekte, analog b-form-select
    textField: { type: String, default: 'text' },
    valueField: { type: String, default: 'value' },
    // Feldname des Gruppentitels, gesetzt zusammen mit groupValues für gruppierte Optionen
    groupLabel: { type: String, default: '' },
    // Feldname der Optionsliste innerhalb einer Gruppe
    groupValues: { type: String, default: '' },
    // immediate meldet jede Änderung sofort, button sammelt sie bis zum Klick auf Anwenden
    applyMode: { type: String, default: 'immediate', validator: mode => APPLY_MODES.includes(mode) },
    // Beschriftung des Anwenden-Buttons
    applyText: { type: String, default: 'Anwenden' },
    // Deaktiviert die gesamte Komponente
    disabled: { type: Boolean, default: false },
    // Erlaubt das Leeren der Auswahl über das X und das Abwählen im Einfachmodus
    clearable: { type: Boolean, default: false },
    // Validierungszustand, false rahmt rot, true grün, null bleibt neutral
    state: { type: Boolean, default: null },
    // Hebt das Feld farblich hervor, etwa für geänderte und noch nicht gespeicherte Werte
    highlight: { type: Boolean, default: false },
    // Höhe und Schriftgröße des Triggers
    size: { type: String, default: 'md', validator: size => SIZES.includes(size) },
    // Sortierung der Optionsliste, none behält die Reihenfolge der übergebenen Optionen
    sort: { type: String, default: 'alphabetisch', validator: mode => SORT_MODES.includes(mode) },
    // Blendet das Suchfeld aus
    hideSearch: { type: Boolean, default: false },
    // Platzhalter des Suchfeldes, leer erzeugt einen Text aus dem Label
    searchPlaceholder: { type: String, default: '' },
    // Klappt die Optionsliste im Textfluss auf statt als schwebendes Menü
    inline: { type: Boolean, default: false },
    // Öffnet das Menü nach oben
    dropup: { type: Boolean, default: false },
    // Maximale Höhe der Optionsliste
    listMaxHeight: { type: String, default: '240px' },
    // Zeigt einen Spinner, für Optionen die asynchron geladen werden
    loading: { type: Boolean, default: false },
    // Bei false übernimmt der Aufrufer die Filterung, etwa bei einer Suche im Backend
    internalSearch: { type: Boolean, default: true },
    // Text ohne jede Option
    emptyText: { type: String, default: 'Keine Optionen' },
    // Text wenn die Suche keine Option übrig lässt
    noResultsText: { type: String, default: 'Suche ergab keine Treffer' },
    // Zusätzliche Klassen für Trigger und Menü
    toggleClass: { type: [String, Array, Object], default: null },
    menuClass: { type: [String, Array, Object], default: null },
    // Id des Wurzelelements, bootstrap-vue leitet daraus die Id des Triggers ab
    id: { type: String, default: null },
  },
  data() {
    const initial = toValueArray(this.value);
    return {
      // Zwischenstand der Auswahl, nur im Modus button in Verwendung
      draftValues: initial,
      // Auswahl beim Öffnen, hält das Vorziehen der gewählten Optionen während der Bearbeitung stabil
      valuesWhenOpened: initial,
      search: '',
      isOpen: false,
      changesSent: false,
      draftChanged: false,
      highlightedIndex: -1,
    };
  },
  computed: {
    baseId() {
      return this.id || `app-select-${this._uid}`;
    },
    listId() {
      return `${this.baseId}__list`;
    },
    highlightedOptionId() {
      return this.highlightedIndex >= 0 ? this.optionId(this.highlightedIndex) : null;
    },
    rootClasses() {
      return [
        `app-select--${this.size}`,
        {
          'app-select--inline': this.inline,
          'app-select--open': this.isOpen,
          'app-select--disabled': this.disabled,
          'app-select--highlight': this.highlight,
          'app-select--valid': this.state === true,
          'app-select--invalid': this.state === false,
        },
      ];
    },
    // Bei gesetztem groupLabel und groupValues werden die Optionen als Gruppen erwartet
    hasGroups() {
      return Boolean(this.groupLabel && this.groupValues);
    },
    warnLabel() {
      return this.label ? ` ${this.label}` : '';
    },
    normalizedOptions() {
      const { textField, valueField, groupLabel, groupValues } = this;
      return buildOptions(this.options, { textField, valueField, groupLabel, groupValues });
    },
    // Alle wählbaren Optionen ohne die Gruppentitel
    selectableOptions() {
      return this.normalizedOptions.filter(option => !option.isGroup);
    },
    optionByValue() {
      const map = new Map();
      this.selectableOptions.forEach(option => {
        if (!map.has(option.value)) {
          map.set(option.value, option);
        }
      });
      return map;
    },
    // Angewendete Auswahl als Array, unabhängig davon ob der Wert Skalar oder Array ist
    selectedValues() {
      return toValueArray(this.value);
    },
    selectedOptions() {
      return this.selectedValues.map(value => this.optionByValue.get(value)).filter(Boolean);
    },
    selectedText() {
      return this.selectedValues
        .map(value => {
          const option = this.optionByValue.get(value);
          return option ? option.text : asText(value);
        })
        .join(', ');
    },
    selectionCount() {
      return this.selectedValues.length;
    },
    // Nur mit Anwenden-Button sammelt die Liste die Änderungen
    // sonst meldet jeder Klick den neuen Wert direkt nach außen
    usesApplyButton() {
      return this.multiple && this.applyMode === 'button';
    },
    // Werte, mit denen die Liste gerade arbeitet
    currentValues() {
      return this.usesApplyButton ? this.draftValues : this.selectedValues;
    },
    currentValueSet() {
      return new Set(this.currentValues);
    },
    // Selektierte Werte ohne passende Option werden als eigene Optionen ergänzt,
    // damit die Auswahl bei asynchron geladenen Optionen sichtbar und abwählbar bleibt
    orphanOptions() {
      if (this.hasGroups) {
        return [];
      }
      return this.currentValues
        .filter(value => !this.optionByValue.has(value))
        .map(value => ({
          isGroup: false,
          value,
          text: asText(value),
          disabled: false,
          key: `orphan|${String(value)}`,
          raw: value,
        }));
    },
    showClear() {
      return this.clearable && !this.disabled && this.selectionCount > 0;
    },
    // Nur die Mehrfachauswahl zeigt Titel und Anzahl, die Einfachauswahl immer den gewählten Text
    showLabelWithCount() {
      return this.multiple && Boolean(this.label) && !this.placeholder;
    },
    effectiveSearchPlaceholder() {
      if (this.searchPlaceholder) {
        return this.searchPlaceholder;
      }
      return this.label ? `${this.label} suchen …` : 'Suchen …';
    },
    // Die Mehrfachauswahl zieht die gewählten Optionen vor, ergänzte Werte stehen davor
    sortedOptions() {
      const { sort, hasGroups, valuesWhenOpened, multiple } = this;
      const sorted = sortOptions(this.normalizedOptions, { sort, hasGroups });
      const pinned = pinSelectedFirst(sorted, {
        values: valuesWhenOpened,
        enabled: multiple && !hasGroups,
      });
      return this.orphanOptions.length ? [...this.orphanOptions, ...pinned] : pinned;
    },
    visibleOptions() {
      const { search, internalSearch, hasGroups } = this;
      return filterOptions(this.sortedOptions, { search, internalSearch, hasGroups });
    },
    // Bei eingegebenem Suchbegriff steht die Treffermeldung, sonst der Leertext
    // Das gilt auch bei internalSearch false, wo der Consumer die Optionen selbst filtert
    emptyMessage() {
      if (this.search.trim()) {
        return this.noResultsText;
      }
      return this.selectableOptions.length ? this.noResultsText : this.emptyText;
    },
    // Werte der aktuell sichtbaren und wählbaren Optionen, Basis der Alle-Aktionen
    visibleSelectableValues() {
      return this.visibleOptions
        .filter(option => !option.isGroup && !option.disabled)
        .map(option => option.value);
    },
    isAllVisibleSelected() {
      const visible = this.visibleSelectableValues;
      return visible.length > 0 && visible.every(value => this.currentValueSet.has(value));
    },
    hasVisibleSelection() {
      return this.visibleSelectableValues.some(value => this.currentValueSet.has(value));
    },
    // Die Alle-Aktionen erscheinen auch dann, wenn nur ergänzte Werte in der Liste stehen
    showBulkActions() {
      return this.multiple && Boolean(this.selectableOptions.length || this.orphanOptions.length);
    },
  },
  watch: {
    value() {
      // Externe Wertänderungen werden übernommen, solange der Draft nicht bearbeitet wurde
      if (!this.draftChanged) {
        this.syncDraft();
      }
      this.warnAboutOptions();
    },
    options() {
      this.warnAboutOptions();
    },
    search(term) {
      // Beim Schließen wird das Suchfeld geleert, das ist keine Eingabe und löst keine Suche aus
      if (this.isOpen) {
        this.$emit('search-change', term);
      }
      this.highlightedIndex = term ? this.visibleOptions.findIndex(option => this.isSelectable(option)) : -1;
      this.scrollHighlightIntoView();
    },
  },
  mounted() {
    this.warnAboutOptions();
  },
  methods: {
    optionId(index) {
      return `${this.baseId}__option-${index}`;
    },
    // Gruppentitel sind nur bei Mehrfachauswahl anklickbar, dort wählen sie die ganze Gruppe
    isSelectable(option) {
      if (option.disabled) {
        return false;
      }
      return option.isGroup ? this.multiple : true;
    },
    // Sichtbare Werte einer Gruppe, Basis für Auswahl und Häkchen des Gruppentitels
    visibleGroupValues(groupIndex) {
      return this.visibleOptions
        .filter(option => !option.isGroup && !option.disabled && option.groupIndex === groupIndex)
        .map(option => option.value);
    },
    // Meldet in der Entwicklung Werte, zu denen keine Option existiert, sowie fehlende value-Felder
    warnAboutOptions() {
      if (process.env.NODE_ENV === 'production' || !this.selectableOptions.length) {
        return;
      }
      const fieldName = this.valueField;
      const optionsWithoutValue = (this.options || []).filter(
        option => !this.hasGroups && option !== null && typeof option === 'object' && !(fieldName in option)
      );
      if (optionsWithoutValue.length) {
        // eslint-disable-next-line no-console
        console.warn(`[AppSelect${this.warnLabel}] Optionen ohne ${fieldName}-Feld`, optionsWithoutValue);
      }
      const unknownValues = this.selectedValues.filter(value => !this.optionByValue.has(value));
      if (unknownValues.length) {
        // eslint-disable-next-line no-console
        console.warn(`[AppSelect${this.warnLabel}] Werte ohne Option`, unknownValues);
      }
    },
    isChecked(option) {
      if (option.isGroup) {
        const values = this.visibleGroupValues(option.groupIndex);
        return values.length > 0 && values.every(value => this.currentValueSet.has(value));
      }
      return this.currentValueSet.has(option.value);
    },
    syncDraft() {
      this.draftValues = this.selectedValues;
      this.draftChanged = false;
    },
    // Übernimmt eine neue Auswahl, je nach Modus in den Draft oder direkt nach außen
    updateSelection(values) {
      if (this.usesApplyButton) {
        this.draftValues = values;
        this.draftChanged = true;
        return;
      }
      this.emitSelection(values);
    },
    emitSelection(values) {
      // Die Mehrfachauswahl meldet ein Array, die Einfachauswahl den Wert selbst
      let payload = null;
      if (this.multiple) {
        payload = values.slice();
      } else if (values.length) {
        payload = values[0];
      }
      this.draftValues = values.slice();
      this.changesSent = true;
      this.draftChanged = false;
      // Die Reihenfolge ist verbindlich, Aufrufer lesen im change-Handler den bereits gesetzten Wert
      this.$emit('input', payload);
      this.$emit('change', payload);
      this.$emit('apply', payload);
    },
    // Ergänzt Werte, bereits gewählte bleiben an ihrer Stelle
    addValues(values) {
      this.updateSelection([...new Set([...this.currentValues, ...values])]);
    },
    removeValues(values) {
      const toRemove = new Set(values);
      this.updateSelection(this.currentValues.filter(value => !toRemove.has(value)));
    },
    // Verteilt den Klick auf die drei Fälle Gruppentitel, Mehrfach- und Einfachauswahl
    toggleOption(option) {
      if (this.disabled || !this.isSelectable(option)) {
        return;
      }
      if (option.isGroup) {
        this.toggleGroup(option);
      } else if (this.multiple) {
        this.toggleMultipleOption(option);
      } else {
        this.chooseSingleOption(option);
        return;
      }
      this.clearSearch();
    },
    toggleMultipleOption(option) {
      if (this.isChecked(option)) {
        this.removeValues([option.value]);
        return;
      }
      this.addValues([option.value]);
    },
    // Im Einfachmodus schließt jeder Klick das Menü, das Abwählen braucht clearable
    chooseSingleOption(option) {
      const isSelected = this.isChecked(option);
      if (!isSelected || this.clearable) {
        this.emitSelection(isSelected ? [] : [option.value]);
      }
      this.hide();
    },
    // Ein Klick auf den Gruppentitel wählt alle sichtbaren Optionen der Gruppe oder entfernt sie
    toggleGroup(group) {
      const values = this.visibleGroupValues(group.groupIndex);
      if (!values.length) {
        return;
      }
      if (this.isChecked(group)) {
        this.removeValues(values);
        return;
      }
      this.addValues(values);
    },
    // Die Alle-Aktionen wirken nur auf die sichtbaren Treffer, ein Suchbegriff wird so respektiert
    selectAllVisible() {
      this.addValues(this.visibleSelectableValues);
    },
    clearVisible() {
      this.removeValues(this.visibleSelectableValues);
    },
    onClear() {
      if (this.disabled) {
        return;
      }
      this.emitSelection([]);
    },
    onApply() {
      this.emitSelection(this.draftValues);
      this.hide();
    },
    hide() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.hide();
      }
    },
    open() {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.show();
      }
    },
    // Schließt das Menü und setzt den Fokus auf das benachbarte Formularfeld
    // Ein benachbartes Select öffnet direkt sein Menü, damit der Cursor in dessen Suche steht
    focusAdjacentControl(backwards) {
      const target = findAdjacentFocusable(this.$el, backwards);
      this.hide();
      if (!target) {
        return;
      }
      target.focus({ preventScroll: true });
      openAppSelectTrigger(target);
    },
    clearSearch() {
      if (this.search) {
        this.search = '';
      }
    },
    onShow() {
      this.syncDraft();
      this.valuesWhenOpened = this.selectedValues;
      this.search = '';
      this.changesSent = false;
      this.isOpen = true;
      this.highlightedIndex = this.visibleOptions.findIndex(
        option => !option.isGroup && this.isChecked(option)
      );
      this.$emit('open');
    },
    onShown() {
      this.$nextTick(() => {
        const input = this.$refs.searchInput && this.$refs.searchInput.$el;
        if (input) {
          // preventScroll verhindert, dass ein scrollender Container beim Fokussieren wegspringt
          input.focus({ preventScroll: true });
        } else if (this.$refs.list) {
          this.$refs.list.focus({ preventScroll: true });
        }
        this.scrollHighlightIntoView();
      });
    },
    onHidden() {
      this.isOpen = false;
      this.search = '';
      this.highlightedIndex = -1;
      if (!this.changesSent) {
        this.syncDraft();
      }
      this.$emit('close');
    },
    // Eigene Tastaturnavigation, da bootstrap-vue nur Elemente mit dropdown-item findet
    // stopPropagation verhindert, dass das Menü die Pfeiltasten zusätzlich verarbeitet
    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          this.stopEvent(event);
          this.moveHighlight(1);
          break;
        case 'ArrowUp':
          this.stopEvent(event);
          this.moveHighlight(-1);
          break;
        case 'Home':
        case 'End':
          // Bei eingegebenem Suchtext bleiben Home und End der Textnavigation vorbehalten
          if (!this.search) {
            this.stopEvent(event);
            this.moveHighlightToEdge(event.key === 'Home' ? 1 : -1);
          }
          break;
        case 'Tab':
          // Tab verlässt das Menü, damit man im Formular weiterspringt
          if (event.ctrlKey || event.metaKey || event.altKey) {
            break;
          }
          this.stopEvent(event);
          this.focusAdjacentControl(event.shiftKey);
          break;
        case 'Enter':
          if (event.ctrlKey || event.metaKey) {
            break;
          }
          this.stopEvent(event);
          this.onEnter();
          break;
        default:
          // Escape bleibt bei bootstrap-vue, das das Menü schließt und den Trigger fokussiert
          break;
      }
    },
    stopEvent(event) {
      event.preventDefault();
      event.stopPropagation();
    },
    onEnter() {
      const option = this.visibleOptions[this.highlightedIndex];
      if (option && this.isSelectable(option)) {
        this.toggleOption(option);
        return;
      }
      if (this.usesApplyButton) {
        this.onApply();
      }
    },
    // Verschiebt die Markierung um einen Schritt und überspringt deaktivierte Optionen
    moveHighlight(step) {
      const count = this.visibleOptions.length;
      if (!count) {
        return;
      }
      let index = this.highlightedIndex;
      for (let attempt = 0; attempt < count; attempt += 1) {
        if (index < 0 || index >= count) {
          index = step > 0 ? 0 : count - 1;
        } else {
          index = (index + step + count) % count;
        }
        if (this.isSelectable(this.visibleOptions[index])) {
          this.highlightedIndex = index;
          this.scrollHighlightIntoView();
          return;
        }
      }
    },
    moveHighlightToEdge(direction) {
      this.highlightedIndex = -1;
      this.moveHighlight(direction);
    },
    scrollHighlightIntoView() {
      this.$nextTick(() => {
        const list = this.$refs.list;
        if (!list || this.highlightedIndex < 0) {
          return;
        }
        const item = list.querySelectorAll('.app-select__option')[this.highlightedIndex];
        if (item && item.scrollIntoView) {
          item.scrollIntoView({ block: 'nearest' });
        }
      });
    },
  },
};
</script>

<style scoped src="./app-select.css"></style>
